import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import './App.css';
import './index.css';
import Navigation from './components/Navigation.jsx';

// 周报导图(Weekly mindmap) 已隐去：前端路由与导航移除（后端路由保留，不占资源）
import FxLabHome from './pages/FxLabHome.jsx';
const Workspace = lazy(() => import('./pages/Workspace.jsx'));
// 盘口放大镜(OrderBook) 已隐去：XAUUSD 数据源暂停，前端路由移除（页面文件保留）
const LeverageCalculator = lazy(() => import('./pages/LeverageCalculator.jsx'));
const Guide = lazy(() => import('./pages/Guide.jsx'));
// 运时大轮盘：独立整屏页面，不套工作台外壳也不挂抽屉导航
const Wheels = lazy(() => import('./pages/Wheels.jsx'));
import { LanguageProvider } from './hooks/useLanguage.jsx';

// 统一工作台面板：期权透镜 / 数据分析 / 财经动态 / 宏观脉搏 / 流动性浮标 / 每日一卦
const WORKSPACE_PAGES = ['option-lens', 'analytics', 'news', 'macro-pulse', 'liquidity', 'fortune'];

const App = () => {
  // Initialize page from URL hash or localStorage; fallback to 'home'
  const getInitialPage = () => {
    try {
      const raw = (window.location.hash || '').replace('#', '').trim();
      const hash = raw.split('?')[0];
      const known = ['home', 'news', 'fortune', 'leverage-calculator', 'guide', 'analytics', 'macro-pulse', 'option-lens', 'liquidity', 'wheels'];
      if (hash && known.includes(hash)) return hash;
      const saved = localStorage.getItem('currentPage');
      if (saved && known.includes(saved)) return saved;
    } catch { /* ignore */ }
    return 'home';
  };
  const [currentPage, setCurrentPage] = useState(getInitialPage);


  // Persist page selection and reflect in URL hash
  useEffect(() => {
    try {
      localStorage.setItem('currentPage', currentPage);
      // 只比对 '?' 之前的页面名：hash 上的查询串（如 #wheels?token=…）要原样留着，
      // 否则懒加载的页面还没挂载，参数就被这里重写掉了。
      const currentHash = (window.location.hash || '').replace('#', '').split('?')[0];
      if (currentHash !== currentPage) {
        window.location.hash = currentPage;
      }
    } catch { /* ignore */ }
  }, [currentPage]);

  // Listen for external hash changes (e.g. TopNav without onNavigate)
  useEffect(() => {
    const known = ['home', 'forum', 'forum-mod', 'predictions', 'news', 'health', 'health-token', 'health-match', 'trading', 'fortune', 'leverage-calculator', 'guide', 'login', 'register', 'withdrawal-rate', 'liquidity-crisis', 'analytics', 'macro-pulse', 'option-lens', 'liquidity', 'wheels'];
    const onHashChange = () => {
      const hash = (window.location.hash || '').replace('#', '').split('?')[0];
      if (hash && known.includes(hash) && hash !== currentPage) {
        setCurrentPage(hash);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [currentPage]);



  return (
    <Routes>
      {/* 周报导图(weekly-mindmap) 路由已隐去 */}

      {/* Existing hash-based app — catch-all */}
      <Route path="*" element={
        <HashApp
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      } />
    </Routes>
  );
};

/**
 * Hash-based app dispatcher.
 * Two design surfaces own their own chrome:
 *   · home            → FxLabHome (marketing landing)
 *   · workspace pages → Workspace shell (sidebar + topbar hosting the panel)
 * Legacy utility pages keep the hamburger Navigation drawer.
 */
const HashApp = ({ currentPage, setCurrentPage }) => {
  if (currentPage === 'home') {
    return <FxLabHome onNavigate={setCurrentPage} />;
  }

  // 运时大轮盘：自带整屏视觉，不加任何外壳
  if (currentPage === 'wheels') {
    return (
      <Suspense fallback={<div className="muted">Loading…</div>}>
        <Wheels />
      </Suspense>
    );
  }

  if (WORKSPACE_PAGES.includes(currentPage)) {
    return (
      <Suspense fallback={<div className="muted">Loading…</div>}>
        <Workspace active={currentPage} onNavigate={setCurrentPage} />
      </Suspense>
    );
  }

  // Legacy utility pages — keep the drawer navigation.
  return (
    <div className="app">
      <Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} />
      {currentPage === 'leverage-calculator' ? (
        <Suspense fallback={<div className="muted">Loading…</div>}>
          <LeverageCalculator />
        </Suspense>
      ) : currentPage === 'guide' ? (
        <Suspense fallback={<div className="muted">Loading…</div>}>
          <Guide onNavigate={setCurrentPage} />
        </Suspense>
      ) : (
        <FxLabHome onNavigate={setCurrentPage} />
      )}
    </div>
  );
};


export default function AppWithLanguage() {
  return (
    <LanguageProvider>
      <App />
    </LanguageProvider>
  );
}
